import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type {
  AuthState,
  LoginCredentials,
  User,
  WhatsAppAuthRequest,
  WhatsAppPINVerification,
  StoredApiCredentials,
} from '../../types/auth.types'
import { authService } from '@services/api/authService'

// Extended auth state for stored credentials
interface ExtendedAuthState extends AuthState {
  storedCredentials: StoredApiCredentials[]
  useStoredCredentials: boolean
  useDemoCredentials: boolean
  showCredentialsForm: boolean
}

// Initial State
const initialState: ExtendedAuthState = {
  user: null,
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,
  // WhatsApp authentication state
  authRequestId: null,
  whatsAppAuthStatus: 'idle',
  isPolling: false,
  // Stored credentials state
  storedCredentials: authService.getAllStoredCredentials(),
  useStoredCredentials: false,
  useDemoCredentials: false,
  showCredentialsForm: false,
}

// Async Thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials)
      return response.user
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed')
    }
  }
)

export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await authService.logout()
  } catch (error: any) {
    return rejectWithValue(error.message || 'Logout failed')
  }
})

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getProfile()
      return user
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch profile')
    }
  }
)

// WhatsApp Authentication Thunks
export const requestWhatsAppAuth = createAsyncThunk(
  'auth/requestWhatsAppAuth',
  async (request: WhatsAppAuthRequest, { rejectWithValue }) => {
    try {
      const response = await authService.requestPIN(request)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send verification')
    }
  }
)

export const checkWhatsAppAuthStatus = createAsyncThunk(
  'auth/checkWhatsAppAuthStatus',
  async (authRequestId: string, { rejectWithValue }) => {
    try {
      const status = await authService.checkAuthStatus(authRequestId)
      return status
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to check status')
    }
  }
)

export const verifyWhatsAppPIN = createAsyncThunk(
  'auth/verifyWhatsAppPIN',
  async (verification: WhatsAppPINVerification, { rejectWithValue }) => {
    try {
      const response = await authService.verifyPIN(verification)
      return response.user
    } catch (error: any) {
      return rejectWithValue(error.message || 'PIN verification failed')
    }
  }
)

// Login with stored credentials
export const loginWithStoredCredentials = createAsyncThunk(
  'auth/loginWithStoredCredentials',
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await authService.loginWithStoredCredentials(phone)
      return response.user
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to login with stored credentials')
    }
  }
)

// Save API credentials for phone number
export const saveApiCredentials = createAsyncThunk(
  'auth/saveApiCredentials',
  async (
    credentials: { phone: string; apiToken: string; userId: string; userName?: string; company?: string },
    { rejectWithValue }
  ) => {
    try {
      const result = authService.saveCredentialsForPhone(
        credentials.phone,
        credentials.apiToken,
        credentials.userId,
        credentials.userName,
        credentials.company
      )
      if (!result.success) {
        return rejectWithValue(result.message || 'Failed to save credentials')
      }
      // Return updated credentials list
      return authService.getAllStoredCredentials()
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save credentials')
    }
  }
)

// Delete stored credentials
export const deleteStoredCredentials = createAsyncThunk(
  'auth/deleteStoredCredentials',
  async (phone: string, { rejectWithValue }) => {
    try {
      const success = authService.deleteStoredCredentials(phone)
      if (!success) {
        return rejectWithValue('Failed to delete credentials')
      }
      return authService.getAllStoredCredentials()
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete credentials')
    }
  }
)

// Request WhatsApp auth with fallback to stored credentials
export const requestWhatsAppAuthWithFallback = createAsyncThunk(
  'auth/requestWhatsAppAuthWithFallback',
  async (request: WhatsAppAuthRequest, { rejectWithValue }) => {
    try {
      const response = await authService.requestPINWithFallback(request)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send verification')
    }
  }
)

// Login with demo credentials (when API unavailable in demo mode)
export const loginWithDemoCredentials = createAsyncThunk(
  'auth/loginWithDemoCredentials',
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await authService.loginWithDemoCredentials(phone)
      return response.user
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to login with demo credentials')
    }
  }
)

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = true
    },
    clearAuth: state => {
      state.user = null
      state.isAuthenticated = false
      state.error = null
      state.authRequestId = null
      state.whatsAppAuthStatus = 'idle'
      state.isPolling = false
    },
    // WhatsApp authentication reducers
    setPolling: (state, action: PayloadAction<boolean>) => {
      state.isPolling = action.payload
    },
    resetWhatsAppAuth: state => {
      state.authRequestId = null
      state.whatsAppAuthStatus = 'idle'
      state.isPolling = false
      state.error = null
      state.useStoredCredentials = false
      state.useDemoCredentials = false
      authService.cancelWhatsAppAuth()
    },
    // Stored credentials reducers
    setShowCredentialsForm: (state, action: PayloadAction<boolean>) => {
      state.showCredentialsForm = action.payload
    },
    setUseStoredCredentials: (state, action: PayloadAction<boolean>) => {
      state.useStoredCredentials = action.payload
    },
    refreshStoredCredentials: state => {
      state.storedCredentials = authService.getAllStoredCredentials()
    },
  },
  extraReducers: builder => {
    // Login
    builder.addCase(login.pending, state => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(login.fulfilled, (state, action) => {
      state.isLoading = false
      state.isAuthenticated = true
      state.user = action.payload
      state.error = null
    })
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false
      state.isAuthenticated = false
      state.user = null
      state.error = action.payload as string
    })

    // Logout
    builder.addCase(logout.pending, state => {
      state.isLoading = true
    })
    builder.addCase(logout.fulfilled, state => {
      state.isLoading = false
      state.isAuthenticated = false
      state.user = null
      state.error = null
    })
    builder.addCase(logout.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    // Fetch Profile
    builder.addCase(fetchProfile.pending, state => {
      state.isLoading = true
    })
    builder.addCase(fetchProfile.fulfilled, (state, action) => {
      state.isLoading = false
      state.user = action.payload
      state.isAuthenticated = true
    })
    builder.addCase(fetchProfile.rejected, (state, action) => {
      state.isLoading = false
      state.isAuthenticated = false
      state.user = null
      state.error = action.payload as string
    })

    // WhatsApp Auth - Request PIN
    builder.addCase(requestWhatsAppAuth.pending, state => {
      state.isLoading = true
      state.error = null
      state.whatsAppAuthStatus = 'idle'
    })
    builder.addCase(requestWhatsAppAuth.fulfilled, (state, action) => {
      state.isLoading = false
      state.authRequestId = action.payload.authRequestId
      state.whatsAppAuthStatus = 'pending'
    })
    builder.addCase(requestWhatsAppAuth.rejected, (state, action) => {
      state.isLoading = false
      state.whatsAppAuthStatus = 'error'
      state.error = action.payload as string
    })

    // WhatsApp Auth - Check Status (Polling)
    builder.addCase(checkWhatsAppAuthStatus.pending, state => {
      state.isPolling = true
    })
    builder.addCase(checkWhatsAppAuthStatus.fulfilled, (state, action) => {
      state.isPolling = false
      const status = action.payload.status

      if (status === 'authenticated') {
        state.whatsAppAuthStatus = 'authenticated'
        state.isAuthenticated = true
        state.user = action.payload.user || null
      } else if (status === 'expired' || status === 'not_found') {
        state.whatsAppAuthStatus = 'expired'
        state.authRequestId = null
      }
      // If 'pending', keep the current state
    })
    builder.addCase(checkWhatsAppAuthStatus.rejected, (state, action) => {
      state.isPolling = false
      state.whatsAppAuthStatus = 'error'
      state.error = action.payload as string
    })

    // WhatsApp Auth - Verify PIN (Manual fallback)
    builder.addCase(verifyWhatsAppPIN.pending, state => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(verifyWhatsAppPIN.fulfilled, (state, action) => {
      state.isLoading = false
      state.isAuthenticated = true
      state.user = action.payload
      state.whatsAppAuthStatus = 'authenticated'
      state.authRequestId = null
    })
    builder.addCase(verifyWhatsAppPIN.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    // Login with stored credentials
    builder.addCase(loginWithStoredCredentials.pending, state => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(loginWithStoredCredentials.fulfilled, (state, action) => {
      state.isLoading = false
      state.isAuthenticated = true
      state.user = action.payload
      state.whatsAppAuthStatus = 'authenticated'
      state.authRequestId = null
      state.useStoredCredentials = false
      state.storedCredentials = authService.getAllStoredCredentials() // Refresh to get updated lastUsed
    })
    builder.addCase(loginWithStoredCredentials.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    // Save API credentials
    builder.addCase(saveApiCredentials.pending, state => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(saveApiCredentials.fulfilled, (state, action) => {
      state.isLoading = false
      state.storedCredentials = action.payload
      state.showCredentialsForm = false
    })
    builder.addCase(saveApiCredentials.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    // Delete stored credentials
    builder.addCase(deleteStoredCredentials.fulfilled, (state, action) => {
      state.storedCredentials = action.payload
    })

    // Request WhatsApp auth with fallback
    builder.addCase(requestWhatsAppAuthWithFallback.pending, state => {
      state.isLoading = true
      state.error = null
      state.whatsAppAuthStatus = 'idle'
      state.useStoredCredentials = false
      state.useDemoCredentials = false
    })
    builder.addCase(requestWhatsAppAuthWithFallback.fulfilled, (state, action) => {
      state.isLoading = false
      state.authRequestId = action.payload.authRequestId
      state.whatsAppAuthStatus = 'pending'
      // Check if we should use stored credentials (fallback mode)
      if (action.payload.useStoredCredentials) {
        state.useStoredCredentials = true
      }
      // Check if demo credentials are available
      if (action.payload.useDemoCredentials) {
        state.useDemoCredentials = true
      }
    })
    builder.addCase(requestWhatsAppAuthWithFallback.rejected, (state, action) => {
      state.isLoading = false
      state.whatsAppAuthStatus = 'error'
      state.error = action.payload as string
    })

    // Login with demo credentials
    builder.addCase(loginWithDemoCredentials.pending, state => {
      state.isLoading = true
      state.error = null
    })
    builder.addCase(loginWithDemoCredentials.fulfilled, (state, action) => {
      state.isLoading = false
      state.isAuthenticated = true
      state.user = action.payload
      state.whatsAppAuthStatus = 'authenticated'
      state.authRequestId = null
      state.useStoredCredentials = false
      state.useDemoCredentials = false
      state.storedCredentials = authService.getAllStoredCredentials() // Refresh to include newly saved credentials
    })
    builder.addCase(loginWithDemoCredentials.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })
  },
})

export const {
  clearError,
  setUser,
  clearAuth,
  setPolling,
  resetWhatsAppAuth,
  setShowCredentialsForm,
  setUseStoredCredentials,
  refreshStoredCredentials,
} = authSlice.actions
export default authSlice.reducer
